import apiService from "../api/api.service";

export interface ParasutListParams {
  [key: string]: any;
}

export interface ManualInvoicePayload {
  companyId: number;
  description: string;
  amount: number;
  currency?: string;
  contactName: string;
  contactEmail?: string;
  contactPhone?: string;
  taxNumber?: string;
  items?: Array<{
    description: string;
    quantity: number;
    unitPrice: number;
    vatRate?: number;
  }>;
}

export interface PaymentPayload {
  amount: number;
  date: string;
  description?: string;
  paymentMethod?: string;
}

class ParasutService {
  // === ParasutController Rotaları (Doğrudan Parasut Etkileşimi) ===

  // Manuel fatura oluşturma
  async createManualInvoice(payload: ManualInvoicePayload): Promise<any> {
    return apiService.post(`/parasut/manual-invoice`, payload);
  }

  // Fatura onaylama
  async approveInvoice(invoiceId: string): Promise<any> {
    return apiService.post(`/parasut/sales-invoices/${invoiceId}/approve`);
  }

  // Tahsilat ekleme
  async addPayment(invoiceId: string, payload: PaymentPayload): Promise<any> {
    return apiService.post(`/parasut/sales-invoices/${invoiceId}/payments`, payload);
  }

  // Fatura detayı getirme
  async getSalesInvoice(invoiceId: string): Promise<any> {
    return apiService.get(`/parasut/sales-invoices/${invoiceId}`);
  }

  // Tüm satış faturalarını listeleme
  async listSalesInvoices(params?: ParasutListParams): Promise<any> {
    return apiService.get(`/parasut/sales-invoices`, { params });
  }

  // Fatura güncelleme
  async updateSalesInvoice(invoiceId: string, payload: any): Promise<any> {
    return apiService.put(`/parasut/sales-invoices/${invoiceId}`, payload);
  }

  // Şirkete göre fatura listeleme
  async listCompanySalesInvoices(companyId: number, params?: ParasutListParams): Promise<any> {
    return apiService.get(`/parasut/companies/${companyId}/sales-invoices`, { params });
  }

  // E-Fatura gelen kutusu kontrol
  async checkEInvoiceInbox(): Promise<any> {
    return apiService.get(`/parasut/e-invoice/inbox`);
  }

  // E-Fatura resmileştirme
  async createEInvoice(invoiceId: string): Promise<any> {
    return apiService.post(`/parasut/sales-invoices/${invoiceId}/e-invoice`);
  }

  // E-Arşiv resmileştirme
  async createEArchive(invoiceId: string): Promise<any> {
    return apiService.post(`/parasut/sales-invoices/${invoiceId}/e-archive`);
  }

  // E-belge işlem durumu sorgulama
  async getOperationStatus(operationId: string): Promise<any> {
    return apiService.get(`/parasut/e-documents/operations/${operationId}`);
  }

  // E-Fatura PDF indirme
  async getEInvoicePdf(eInvoiceId: string): Promise<any> {
    return apiService.get(`/parasut/e-archives/${eInvoiceId}/pdf`, { 
      responseType: 'blob' 
    });
  }

  // E-Arşiv PDF indirme
  async getEArchivePdf(eArchiveId: string): Promise<any> {
    return apiService.get(`/parasut/e-archives/${eArchiveId}/pdf`, { 
      responseType: 'blob' 
    });
  }

  // Otomatik resmileştirme (tüm adımları tek çağrıda)
  async officializeInvoice(invoiceId: string): Promise<any> {
    return apiService.post(`/parasut/sales-invoices/${invoiceId}/officialize`);
  }

  // Ödeme sonrası fatura oluşturma
  async createInvoiceAfterPayment(payload: any): Promise<any> {
    return apiService.post(`/parasut/sales-invoices/after-payment`, payload);
  }

  // === ParasutInvoiceRecordController Rotaları (Lokal Veritabanı Yönetimi) ===

  // Tüm fatura kayıtlarını listeleme
  async getInvoiceRecords(params?: ParasutListParams): Promise<any> {
    return apiService.get(`/parasut/invoice-records`, { params });
  }

  // ID ile fatura kaydı getirme
  async getInvoiceRecord(id: number): Promise<any> {
    return apiService.get(`/parasut/invoice-records/${id}`);
  }

  // Parasut ID ile fatura kaydı getirme
  async getInvoiceRecordByParasutId(invoiceId: string): Promise<any> {
    return apiService.get(`/parasut/invoice-records/by-parasut/${invoiceId}`);
  }

  // Şirkete göre fatura kayıtları
  async getCompanyInvoiceRecords(companyId: number, params?: ParasutListParams): Promise<any> {
    return apiService.get(`/parasut/invoice-records/by-company/${companyId}`, { params });
  }

  // Fatura kaydı durumu güncelleme
  async updateInvoiceRecordStatus(id: number, status: string): Promise<any> {
    return apiService.patch(`/parasut/invoice-records/${id}/status`, { status });
  }

  // Fatura kaydını güncelleme
  async updateInvoiceRecord(id: number, payload: any): Promise<any> {
    return apiService.put(`/parasut/invoice-records/${id}`, payload);
  }

  // Fatura kaydını silme
  async deleteInvoiceRecord(id: number): Promise<any> {
    return apiService.delete(`/parasut/invoice-records/${id}`);
  }

  // Parasut ID ile fatura resmileştirme
  async officializeInvoiceByParasutId(invoiceId: string): Promise<any> {
    return apiService.post(`/parasut/invoice-records/by-parasut/${invoiceId}/officialize`);
  }

  // === Yardımcı Metodlar ===

  // PDF indirme yardımcısı
  async downloadPdf(url: string, filename: string): Promise<void> {
    try {
      const response: any = await apiService.get(url);
      const downloadUrl = response.data.attributes.url;
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      throw error;
    }
  }

  // İşlem durumu takibi (polling)
  async pollOperationStatus(operationId: string, maxAttempts: number = 30, interval: number = 2000): Promise<any> {
    
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const status = await this.getOperationStatus(operationId);
        
        if (status.status === 'completed' || status.status === 'failed') {
          return status;
        }
        
        if (attempt < maxAttempts) {
          await new Promise(resolve => setTimeout(resolve, interval));
        }
      } catch (error) {
        if (attempt === maxAttempts) throw error;
      }
    }
    
    throw new Error(`İşlem durumu takibi zaman aşımına uğradı: ${operationId}`);
  }
}

const parasutService = new ParasutService();
export default parasutService;


